// Reservation / front-desk DTOs — PRD §3.3–3.13.
import { z } from 'zod';
import { dateSchema, moneySchema, uuidSchema } from './common.js';
export const reservationSourceSchema = z.enum([
    'walk_in',
    'phone',
    'email',
    'whatsapp',
    'website',
    'ota',
    'travel_agent',
    'corporate',
    'channel', // ADR-0022: created from a cloud channel booking
    'other',
]);
export const reservationRoomInputSchema = z.object({
    roomTypeId: uuidSchema,
    adults: z.number().int().min(1).max(10),
    children: z.number().int().min(0).max(10).default(0),
    ratePlanId: uuidSchema.optional(),
    roomId: uuidSchema.optional(),
    /** manual override of all nightly amounts (rate.override + reason) */
    overrideNightlyAmount: moneySchema.shape.amount.optional(),
    overrideReason: z.string().trim().min(3).max(200).optional(),
});
export const createReservationSchema = z
    .object({
    primaryGuestId: uuidSchema,
    source: reservationSourceSchema,
    channelRef: z.string().max(80).nullable().optional(),
    arrival: dateSchema,
    departure: dateSchema,
    ratePlanId: uuidSchema,
    rooms: z.array(reservationRoomInputSchema).min(1).max(20),
    guarantee: z.enum(['none', 'deposit', 'company', 'credit_card']).default('none'),
    guaranteeReference: z.string().max(80).nullable().optional(),
    /** tentative when no guarantee and not walk-in; hold expiry from property settings */
    tentative: z.boolean().default(false),
    notes: z
        .array(z.object({
        kind: z.enum(['guest_request', 'internal', 'housekeeping', 'billing']),
        text: z.string().max(1000),
    }))
        .default([]),
    allowOverbooking: z.boolean().default(false),
    /** v1.1 GRP: a pick-up from a group block (set by the rooming list, never by clients) */
    groupBlockId: uuidSchema.optional(),
    /** ADR-0022 (never by clients): a cloud booking applied from a command; `fromDelegation` consumes the
     *  delegated budget instead of checking availability; `requested` creates a request holding no inventory */
    channel: z
        .object({
        channelCode: z.string().min(1).max(32),
        cloudBookingId: uuidSchema,
        fromDelegation: z.boolean(),
        requested: z.boolean(),
        requestedUntil: z.iso.datetime().nullable(),
    })
        .optional(),
})
    .refine((v) => v.departure > v.arrival, 'departure must be after arrival');
export const modifyReservationSchema = z
    .object({
    arrival: dateSchema.optional(),
    departure: dateSchema.optional(),
    adults: z.number().int().min(1).max(10).optional(),
    children: z.number().int().min(0).max(10).optional(),
    ratePlanId: uuidSchema.optional(),
    allowOverbooking: z.boolean().default(false),
    rowVersion: z.number().int().positive(),
})
    .refine((v) => !v.arrival || !v.departure || v.departure > v.arrival, 'departure must be after arrival');
export const reasonSchema = z.object({
    reasonCode: z.string().trim().min(2).max(40),
    reasonText: z.string().max(500).optional(),
});
export const assignRoomSchema = z.object({
    roomId: uuidSchema,
    lock: z.boolean().default(false),
    allowDirty: z.boolean().default(false),
});
export const checkInSchema = z.object({
    guestIds: z.array(uuidSchema).min(1).max(10),
    roomId: uuidSchema.optional(),
    allowDirtyRoom: z.boolean().default(false),
    allowIdentityPending: z.boolean().default(false),
    allowBeforeArrival: z.boolean().default(false),
});
export const extendStaySchema = z.object({
    newDeparture: dateSchema,
    ratePlanId: uuidSchema.optional(),
});
export const roomMoveSchema = z.object({
    toRoomId: uuidSchema,
    reason: z.string().trim().min(3).max(300),
    reprice: z.boolean().default(false),
    allowDirty: z.boolean().default(false),
});
export const availabilityQuerySchema = z
    .object({
    from: dateSchema,
    to: dateSchema,
    roomTypeId: uuidSchema.optional(),
})
    .refine((v) => v.to > v.from, 'to must be after from');
export const reservationSearchSchema = z.object({
    q: z.string().trim().max(80).optional(),
    status: z
        .enum([
        'requested',
        'tentative',
        'confirmed',
        'checked_in',
        'checked_out',
        'cancelled',
        'no_show',
    ])
        .optional(),
    arrivalFrom: dateSchema.optional(),
    arrivalTo: dateSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** Guest signature on the registration card (v1.1): pen strokes as flat [x0,y0,x1,y1,…] in the pad's pixel space. */
export const staySignatureSchema = z.object({
    width: z.number().int().min(50).max(4000),
    height: z.number().int().min(30).max(2000),
    strokes: z
        .array(z.array(z.number().finite()).min(4).max(2000))
        .min(1)
        .max(200)
        .refine((s) => s.every((p) => p.length % 2 === 0), 'strokes are x,y pairs'),
});
//# sourceMappingURL=reservation.js.map